import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import api from "../../../apiClient";
import { cartoTileUrl, CARTO_ATTRIBUTION } from "../../../lib/basemap";

/* ─── branded map styles ────────────────────────────────────────────────── */

const BRANDED_STYLE = document.createElement("style");
BRANDED_STYLE.textContent = `
  .comp-map-branded .base-tiles {
    filter: saturate(0) brightness(0.92) contrast(1.05);
  }
  .comp-map-branded .label-tiles {
    filter: saturate(0.6) sepia(0.5) hue-rotate(185deg) brightness(0.55) contrast(1.6);
  }
`;
if (!document.head.querySelector("[data-comp-map-style]")) {
  BRANDED_STYLE.setAttribute("data-comp-map-style", "");
  document.head.appendChild(BRANDED_STYLE);
}

/* ─── numbered pin icon factory ─────────────────────────────────────────── */

const SUBJECT_COLOR = "#0E1B3D"; // packet dark navy
const COMP_COLOR    = "#6E84C7"; // packet accent blue
const PIN_SIZE      = 28;

function makeIcon(label, isSubject) {
  const bg = isSubject ? SUBJECT_COLOR : COMP_COLOR;
  const size = isSubject ? PIN_SIZE + 4 : PIN_SIZE;
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${bg};color:#fff;border:2px solid #fff;
      display:flex;align-items:center;justify-content:center;
      font-weight:700;font-size:${isSubject ? 13 : 12}px;
      box-shadow:0 2px 6px rgba(0,0,0,.35);
      user-select:none;
    ">${label}</div>`,
  });
}

/* ─── auto-fit helper ───────────────────────────────────────────────────── */

function FitBounds({ pins, savedCenter, savedZoom }) {
  const map = useMap();
  const fittedRef = useRef(false);
  useEffect(() => {
    if (fittedRef.current) return; // only auto-fit once
    const valid = pins.filter((p) => p.lat != null && p.lng != null);
    if (!valid.length) return;

    fittedRef.current = true;

    // If reopening a saved map, restore exact view
    if (savedCenter && savedZoom) {
      map.setView(savedCenter, savedZoom);
      return;
    }

    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 15 });
  }, [pins, map, savedCenter, savedZoom]);
  return null;
}

/* ─── draggable marker wrapper ──────────────────────────────────────────── */

function DraggableMarker({ pin, onDragEnd }) {
  const markerRef = useRef(null);
  const handleDragEnd = useCallback(() => {
    const m = markerRef.current;
    if (!m) return;
    const { lat, lng } = m.getLatLng();
    onDragEnd(pin.id, lat, lng);
  }, [pin.id, onDragEnd]);

  if (pin.lat == null || pin.lng == null) return null;

  return (
    <Marker
      ref={markerRef}
      position={[pin.lat, pin.lng]}
      icon={makeIcon(pin.label, pin.isSubject)}
      draggable
      eventHandlers={{ dragend: handleDragEnd }}
    >
      <Tooltip direction="top" offset={[0, -16]} permanent={false}>
        <span className="text-xs font-medium">
          {pin.isSubject ? "Subject" : `Comp ${pin.label}`}: {pin.account || pin.address}
        </span>
      </Tooltip>
    </Marker>
  );
}

/* ─── main modal ────────────────────────────────────────────────────────── */

export default function CompMapModal({ open, onClose, onSave, rows, subjectId, assessor, state, existingMapData }) {
  const [pins, setPins] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeErrors, setGeocodeErrors] = useState(0);
  const mapRef = useRef(null);

  // Build initial pins from rows (subject + used comps)
  const mappableRows = useMemo(() => {
    if (!rows?.length) return [];
    const subject = rows.find((r) => r.id === subjectId);
    const usedComps = rows.filter((r) => r.id !== subjectId && r.use);
    const out = [];
    if (subject) {
      out.push({ ...subject, _label: "S", _isSubject: true });
    }
    usedComps.forEach((r, i) => {
      out.push({ ...r, _label: String(i + 1), _isSubject: false });
    });
    return out;
  }, [rows, subjectId]);

  // Track the active geocode request so stale responses (from React Strict Mode
  // double-firing or rapid re-opens) are ignored.
  const geocodeGenRef = useRef(0);

  // Initialize pins from existing data or from rows, then geocode
  useEffect(() => {
    if (!open) return;

    const gen = ++geocodeGenRef.current; // bump generation

    let initialPins;
    if (existingMapData?.pins?.length) {
      const existing = new Map(existingMapData.pins.map((p) => [p.id, p]));
      initialPins = mappableRows.map((r) => {
        const saved = existing.get(r.id);
        return {
          id: r.id,
          label: r._label,
          isSubject: r._isSubject,
          account: r.account || "",
          address: r.address || "",
          addressOverride: saved?.addressOverride || "",
          lat: saved?.lat ?? null,
          lng: saved?.lng ?? null,
        };
      });
    } else {
      initialPins = mappableRows.map((r) => ({
        id: r.id,
        label: r._label,
        isSubject: r._isSubject,
        account: r.account || "",
        address: r.address || "",
        addressOverride: "",
        lat: null,
        lng: null,
      }));
    }

    setPins(initialPins);

    // Geocode any pins missing coordinates
    const toGeocode = initialPins.filter((p) => p.lat == null);
    if (toGeocode.length) {
      _doGeocode(toGeocode, gen);
    }

    return () => { geocodeGenRef.current++; }; // abort on cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Geocode helper — checks generation to discard stale responses
  const _doGeocode = useCallback(async (toGeocode, gen) => {
    const addresses = toGeocode
      .filter((p) => (p.addressOverride || p.address || "").trim())
      .map((p) => ({
        id: p.id,
        address: (p.addressOverride || p.address).trim(),
      }));

    if (!addresses.length) return;

    setGeocoding(true);
    setGeocodeErrors(0);
    try {
      // Long timeout: 22 addresses × 1.5s/each = ~33s; allow up to 2 minutes
      const resp = await api.post("/bapb/geocode", {
        addresses,
        county_hint: assessor || null,
        state_hint: state || null,
      }, { timeout: 120000 });

      // If a newer effect run has started, discard this response
      if (gen !== geocodeGenRef.current) return;

      const results = resp?.data?.results || [];
      const coordMap = new Map(results.map((r) => [r.id, r]));

      setPins((prev) =>
        prev.map((p) => {
          const result = coordMap.get(p.id);
          if (!result) return p;
          return {
            ...p,
            lat: result.ok ? result.lat : p.lat,
            lng: result.ok ? result.lng : p.lng,
          };
        })
      );

      const errCount = results.filter((r) => !r.ok).length;
      setGeocodeErrors(errCount);
    } catch (e) {
      if (gen !== geocodeGenRef.current) return;
      console.error("Geocode failed:", e);
      setGeocodeErrors(toGeocode.length);
    } finally {
      if (gen === geocodeGenRef.current) setGeocoding(false);
    }
  }, [assessor, state]);

  // Convenience wrapper for single-pin re-geocode from the UI
  const geocodePins = useCallback((toGeocode) => {
    _doGeocode(toGeocode, geocodeGenRef.current);
  }, [_doGeocode]);

  // Pin drag handler
  const handleDragEnd = useCallback((id, lat, lng) => {
    setPins((prev) => prev.map((p) => (p.id === id ? { ...p, lat, lng } : p)));
  }, []);

  // Address override + re-geocode for a single pin
  const handleOverrideChange = useCallback((id, value) => {
    setPins((prev) => prev.map((p) => (p.id === id ? { ...p, addressOverride: value } : p)));
  }, []);

  const handleReGeocode = useCallback((pin) => {
    const target = pins.find((p) => p.id === pin.id) || pin;
    geocodePins([target]);
  }, [pins, geocodePins]);

  // Save
  const handleSave = useCallback(() => {
    const map = mapRef.current;
    let center = [32.0, -96.8]; // fallback
    let zoom = 10;
    if (map) {
      const c = map.getCenter();
      center = [c.lat, c.lng];
      zoom = map.getZoom();
    }

    const mapData = {
      pins: pins.map((p) => ({
        id: p.id,
        label: p.label,
        isSubject: p.isSubject,
        account: p.account,
        address: p.address,
        addressOverride: p.addressOverride || "",
        lat: p.lat,
        lng: p.lng,
      })),
      center,
      zoom,
    };
    onSave(mapData);
  }, [pins, onSave]);

  if (!open) return null;

  const validPins = pins.filter((p) => p.lat != null && p.lng != null);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/40" onClick={onClose}>
      <div
        className="m-4 flex flex-1 flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <div className="text-base font-semibold text-slate-800">Comp Map Builder</div>
            <div className="text-xs text-slate-500">
              {pins.length} pin{pins.length !== 1 ? "s" : ""} &middot;{" "}
              {geocoding ? (
                <span className="text-amber-600">Geocoding addresses...</span>
              ) : geocodeErrors > 0 ? (
                <span className="text-rose-600">{geocodeErrors} address{geocodeErrors !== 1 ? "es" : ""} could not be located</span>
              ) : validPins.length === pins.length ? (
                <span className="text-emerald-600">All addresses located</span>
              ) : (
                <span className="text-slate-400">Some addresses pending</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="rounded-lg border border-slate-300 bg-white px-4 py-1.5 text-sm hover:border-slate-400"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={geocoding}
              className="rounded-lg border border-emerald-400 bg-emerald-600 px-4 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Save &amp; Close
            </button>
          </div>
        </div>

        {/* Body: Map + Legend */}
        <div className="flex flex-1 min-h-0">
          {/* Map */}
          <div className="flex-1 relative">
            {pins.length > 0 ? (
              <MapContainer
                ref={mapRef}
                center={[32.0, -96.8]}
                zoom={10}
                zoomDelta={0.5}
                zoomSnap={0.5}
                className="h-full w-full comp-map-branded"
                style={{ height: "100%", width: "100%" }}
              >
                {/* Gray base (land, water, parks — no labels) */}
                <TileLayer
                  url={cartoTileUrl("light_nolabels")}
                  className="base-tiles"
                />
                {/* Roads + labels on top — tinted dark blue */}
                <TileLayer
                  attribution={CARTO_ATTRIBUTION}
                  url={cartoTileUrl("light_only_labels")}
                  className="label-tiles"
                />
                <FitBounds pins={validPins} savedCenter={existingMapData?.center} savedZoom={existingMapData?.zoom} />
                {pins.map((pin) => (
                  <DraggableMarker key={pin.id} pin={pin} onDragEnd={handleDragEnd} />
                ))}
              </MapContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-slate-400">
                No addresses to map
              </div>
            )}
            {geocoding && (
              <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/60">
                <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-lg border border-slate-200">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
                  <span className="text-sm text-slate-600">Geocoding addresses...</span>
                </div>
              </div>
            )}
          </div>

          {/* Legend / Pin list */}
          <div className="w-[300px] border-l border-slate-200 bg-slate-50 flex flex-col min-h-0">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-700">Legend</div>
              <div className="text-xs text-slate-500">Drag pins on map to reposition. Edit address to re-geocode.</div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {pins.map((pin) => (
                <div
                  key={pin.id}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  {/* Pin header */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <div
                      className="flex items-center justify-center rounded-full text-white text-xs font-bold"
                      style={{
                        width: 24, height: 24,
                        background: pin.isSubject ? SUBJECT_COLOR : COMP_COLOR,
                      }}
                    >
                      {pin.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-700 truncate">
                        {pin.isSubject ? "Subject" : `Comp ${pin.label}`}
                      </div>
                      {pin.account && (
                        <div className="text-[10px] text-slate-400 truncate">{pin.account}</div>
                      )}
                    </div>
                    {pin.lat != null ? (
                      <div className="text-[10px] text-emerald-500" title={`${pin.lat?.toFixed(4)}, ${pin.lng?.toFixed(4)}`}>&#10003;</div>
                    ) : (
                      <div className="text-[10px] text-rose-400" title="Not located">&#10007;</div>
                    )}
                  </div>

                  {/* Original address */}
                  <div className="text-[11px] text-slate-500 mb-1 truncate" title={pin.address}>
                    {pin.address || "(no address)"}
                  </div>

                  {/* Override input */}
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      placeholder="Override address..."
                      value={pin.addressOverride || ""}
                      onChange={(e) => handleOverrideChange(pin.id, e.target.value)}
                      className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs placeholder:text-slate-300 focus:border-slate-400 focus:outline-none"
                    />
                    <button
                      onClick={() => handleReGeocode(pin)}
                      disabled={geocoding}
                      className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:border-slate-400 disabled:opacity-40"
                      title="Re-geocode this address"
                    >
                      &#8635;
                    </button>
                  </div>

                  {/* Coordinates (small) */}
                  {pin.lat != null && (
                    <div className="text-[10px] text-slate-400 mt-1">
                      {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
                    </div>
                  )}
                </div>
              ))}

              {pins.length === 0 && (
                <div className="text-xs text-slate-400 text-center py-4">
                  No comps with "Use" checked
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
